import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStore } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  type GetProductAreasGQLResponse,
  type ProductAreaSettings,
  saveAIVisibilitySettings,
} from '@/api/aiVisibility';
import { useCurrentInstance } from '@/api/organization';
import { useAppForm } from '@/components/Form';
import Prompt from '@/components/Prompt';
import { Text, Title } from '@/components/typography';
import { CANARY_ERROR_CODES } from '@/error/messages/canary-standard-errors';
import SolAPIError from '@/error/SolAPIError';
import { toTanStackFormFieldErrors } from '@/error/tanstack-form-field-errors';
import { useNotification } from '@/providers/Notification';

import CompetitorsPanel from './CompetitorsPanel';
import ProductAreasPanel from './ProductAreasPanel';
import SuggestProductAreasDialog from './SuggestProductAreasDialog';
import {
  formValuesFromSavedSettings,
  getAIVisibilitySettingsFormSchema,
} from './useAIVisibilitySettingsForm';

type AIVisibilitySettingsFormProps = {
  domain: string;
  settings: ProductAreaSettings;
  mutate: (data?: GetProductAreasGQLResponse, opts?: { revalidate?: boolean }) => void;
  onWorkflowStarted?: () => void;
};

const AIVisibilitySettingsForm = ({
  domain,
  settings,
  mutate,
  onWorkflowStarted,
}: AIVisibilitySettingsFormProps) => {
  const { instance, mutate: mutateCurrentInstance } = useCurrentInstance();
  const queryClient = useQueryClient();
  const { pushNotification, removeNotification } = useNotification();
  const [isSuggestDialogOpen, setIsSuggestDialogOpen] = useState(false);
  const formSchema = getAIVisibilitySettingsFormSchema();

  const form = useAppForm({
    defaultValues: formValuesFromSavedSettings(settings),
    validators: {
      onChange: formSchema,
      onSubmit: formSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        removeNotification({ type: 'success' });
        removeNotification({ type: 'error' });
        const { brandProductAreas, competitors, showCompetitors } = value;
        const workflowStartedKey = ['aiVisibilityWorkflowStarted', instance?.id];
        const isOverride =
          instance != null &&
          instance.firstAIVisibilityRunDate == null &&
          queryClient.getQueryData<boolean>(workflowStartedKey) !== true;

        try {
          const { success } = await saveAIVisibilitySettings({
            brandProductAreas,
            competitors,
            isOverride,
          });
          if (!success) {
            throw new Error();
          }
          if (isOverride) {
            queryClient.setQueryDefaults(workflowStartedKey, { gcTime: Infinity });
            queryClient.setQueryData(workflowStartedKey, true);
            onWorkflowStarted?.();
          }
          mutate(
            {
              productAreas: {
                get: { brandProductAreas, competitors },
              },
            },
            { revalidate: false },
          );
          mutateCurrentInstance();
          formApi.reset(
            {
              ...formValuesFromSavedSettings({ brandProductAreas, competitors }),
              showCompetitors,
            },
            { keepDefaultValues: true },
          );
          pushNotification({
            type: 'success',
            message: isOverride
              ? t`Success! AI Visibility settings have been saved and a new refresh has been started.`
              : t`Success! AI Visibility settings have been saved. These will be reflected during the next refresh.`,
          });
        } catch (error) {
          const fieldErrors = toTanStackFormFieldErrors(error);
          if (fieldErrors) {
            return fieldErrors;
          }

          const productAreaDetailsError =
            error instanceof SolAPIError
              ? error.errors[CANARY_ERROR_CODES.PRODUCT_AREA_DETAILS_UNAVAILABLE]
              : undefined;

          pushNotification({
            type: 'error',
            message:
              productAreaDetailsError?.message ??
              t`Sorry, we were unable to save your AI Visibility settings. Please review your changes and try again.`,
          });
        }
      },
    },
  });

  const { isDirty, canSave, showCompetitors, brandProductAreaCount } = useStore(
    form.store,
    (state) => ({
      isDirty: state.isDirty,
      canSave: state.values.showCompetitors && state.canSubmit,
      showCompetitors: state.values.showCompetitors,
      brandProductAreaCount: state.values.brandProductAreas.length,
    }),
  );

  const handleConfirmSuggestedProductAreas = (nextProductAreas: string[]) => {
    form.setFieldValue('brandProductAreas', nextProductAreas);
    form.getFieldValue('competitors').forEach((_, index) => {
      form.setFieldValue(`competitors[${index}].comparedProductAreas`, [...nextProductAreas]);
    });
    form.setFieldValue('newProductArea', '');
    setIsSuggestDialogOpen(false);
  };

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (form.state.isSubmitting) {
          return;
        }
        await form.handleSubmit();
      }}
    >
      <form.AppForm>
        <Prompt
          message={t`Are you sure you want to exit without saving your AI Visibility settings?`}
          shouldPreventNavigation={isDirty}
        />
        <div className="sticky top-0 z-10 -mx-8 flex justify-end bg-white px-8 pt-6">
          <form.SubmitButton variant="primary" disabled={!canSave}>
            <Trans>Save</Trans>
          </form.SubmitButton>
        </div>
        <div className="flex w-full max-w-3xl flex-col gap-6">
          <div>
            <Title level={2}>
              <Trans>Map your company footprint against the competition</Trans>
            </Title>
            <Text variant="body1" color="grey" className="mt-2 block">
              <Trans>
                Choose at least 2 and up to 5 product areas. We'll compare your brand voice against
                competitors in these categories.
              </Trans>
            </Text>
          </div>
          <ProductAreasPanel domain={domain} onSuggest={() => setIsSuggestDialogOpen(true)} />
          {showCompetitors && <CompetitorsPanel />}
          {settings.brandProductAreas.length > 0 && (
            <Text variant="body2" color="grey">
              <Trans>
                Your settings are saved. AI Visibility refreshes weekly using your latest settings.
                Results typically take 15–30 minutes to update once your refresh reaches the front
                of the queue. Please check back later for updated results.
              </Trans>
            </Text>
          )}
        </div>
      </form.AppForm>
      {isSuggestDialogOpen && (
        <SuggestProductAreasDialog
          domain={domain}
          existingProductAreaCount={brandProductAreaCount}
          onCancel={() => setIsSuggestDialogOpen(false)}
          onConfirm={handleConfirmSuggestedProductAreas}
        />
      )}
    </form>
  );
};

export default AIVisibilitySettingsForm;
